// src/components/agents/agent-runs-page.tsx
import React, { useEffect, useState, useCallback } from "react";
import {
  History, Loader2, AlertCircle, Clock, BarChart2,
  CheckCircle2, XCircle, ChevronDown, ChevronUp,
  Search, GitBranch,
} from "lucide-react";
import { AgentRunSummary, AgentRunDetail, agentsApi } from "@/app/services/agents/agents-api";

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function ScorePill({ score }: { score: number | null }) {
  if (score === null || score === undefined) return null;
  const n = Math.round(score);
  const color = n >= 75 ? "#10B981" : n >= 50 ? "#F59E0B" : "#EF4444";
  return (
    <span
      className="whitespace-nowrap"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 9px",
        borderRadius: 999,
        backgroundColor: `${color}15`,
        border: `1px solid ${color}30`,
        color,
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      <BarChart2 style={{ width: 10, height: 10 }} />
      {n}/100
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const ok = status === "complete";
  return (
    <span
      className="whitespace-nowrap"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 8px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        backgroundColor: ok ? "rgba(16,185,129,0.08)" : "rgba(239,68,68,0.08)",
        color: ok ? "#10B981" : "#EF4444",
        border: `1px solid ${ok ? "rgba(16,185,129,0.2)" : "rgba(239,68,68,0.2)"}`,
      }}
    >
      {ok ? <CheckCircle2 style={{ width: 10, height: 10 }} /> : <XCircle style={{ width: 10, height: 10 }} />}
      {status}
    </span>
  );
}

function RunRow({ run }: { run: AgentRunSummary }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState<AgentRunDetail | null>(null);
  const [loading, setLoading] = useState(false);

  const toggleDetail = async () => {
    if (expanded) { setExpanded(false); return; }
    if (!detail) {
      setLoading(true);
      try { setDetail(await agentsApi.getRun(run.id)); } catch {}
      setLoading(false);
    }
    setExpanded(true);
  };

  return (
    <div
      style={{
        borderRadius: 12,
        border: "1px solid var(--border)",
        overflow: "hidden",
        backgroundColor: "var(--card)",
      }}
    >
      {/* Row header — single row always, score+date hidden below sm */}
      <button
        onClick={toggleDetail}
        className="w-full flex items-center gap-3 md:gap-4 px-3 md:px-4 py-3 md:py-4 text-left"
        style={{
          backgroundColor: "transparent",
          border: "none",
          cursor: "pointer",
        }}
      >
        {/* Workflow + brand — primary info */}
        <div className="flex-1 min-w-0">
          <div
            style={{
              fontWeight: 600,
              fontSize: 14,
              color: "var(--foreground)",
              marginBottom: 3,
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            {run.agent_name || "Workflow Run"}
          </div>
          <div
            className="flex items-center gap-x-2 gap-y-1 flex-wrap"
            style={{ fontSize: 12, color: "var(--muted-foreground)" }}
          >
            <span
              style={{
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {run.brand_name}
            </span>
            {(run.models_used || []).length > 0 && (
              <>
                <span>·</span>
                <div className="flex items-center gap-1 flex-wrap">
                  {(run.models_used || []).map((m) => (
                    <span
                      key={m}
                      className="whitespace-nowrap"
                      style={{
                        padding: "1px 7px",
                        borderRadius: 999,
                        fontSize: 11,
                        backgroundColor: "var(--muted)",
                        border: "1px solid var(--border)",
                        color: "var(--muted-foreground)",
                        textTransform: "capitalize",
                      }}
                    >
                      {m}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Score — hidden on mobile, shown sm+ */}
        <div className="hidden sm:block flex-shrink-0">
          <ScorePill score={run.perception_score} />
        </div>

        {/* Status — always visible */}
        <div className="flex-shrink-0">
          <StatusBadge status={run.status} />
        </div>

        {/* Date — hidden on mobile, shown md+ */}
        <div
          className="hidden md:flex items-center gap-1 flex-shrink-0"
          style={{ color: "var(--muted-foreground)" }}
        >
          <Clock style={{ width: 12, height: 12 }} />
          <span style={{ fontSize: 12, whiteSpace: "nowrap" }}>
            {formatDate(run.created_at)}
          </span>
        </div>

        {/* Chevron / loader — always visible */}
        <div className="flex-shrink-0">
          {loading ? (
            <Loader2
              style={{ width: 16, height: 16, color: "var(--muted-foreground)" }}
              className="animate-spin"
            />
          ) : expanded ? (
            <ChevronUp
              style={{ width: 16, height: 16, color: "var(--muted-foreground)" }}
            />
          ) : (
            <ChevronDown
              style={{ width: 16, height: 16, color: "var(--muted-foreground)" }}
            />
          )}
        </div>
      </button>

      {/* Expanded detail */}
      {expanded && detail && (
        <div
          className="border-t px-4 md:px-[18px] py-4 flex flex-col gap-3.5"
          style={{
            borderColor: "var(--border)",
            backgroundColor: "var(--muted-10)",
          }}
        >
          {/* Mobile-only: surface score + date here since they're hidden in header */}
          <div className="flex md:hidden items-center gap-x-3 gap-y-2 flex-wrap">
            <ScorePill score={run.perception_score} />
            <div
              className="flex items-center gap-1"
              style={{ color: "var(--muted-foreground)" }}
            >
              <Clock style={{ width: 12, height: 12 }} />
              <span style={{ fontSize: 12 }}>{formatDate(run.created_at)}</span>
            </div>
          </div>

          {/* Prompt */}
          <div>
            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--muted-foreground)",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                marginBottom: 6,
              }}
            >
              Prompt Used
            </p>
            <p
              style={{
                fontSize: 13,
                color: "var(--muted-foreground)",
                lineHeight: 1.6,
                padding: "10px 12px",
                backgroundColor: "var(--muted)",
                borderRadius: 8,
                border: "1px solid var(--border)",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {detail.prompt_used}
            </p>
          </div>

          {/* Summary */}
          {detail.aggregated_results?.consensus_summary && (
            <div>
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "var(--muted-foreground)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  marginBottom: 6,
                }}
              >
                Summary
              </p>
              <p
                style={{
                  fontSize: 13,
                  lineHeight: 1.6,
                  color: "var(--foreground)",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {detail.aggregated_results.consensus_summary}
              </p>
            </div>
          )}

          {/* Key findings */}
          {(detail.aggregated_results?.key_findings || []).length > 0 && (
            <div>
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "var(--muted-foreground)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  marginBottom: 6,
                }}
              >
                Key Findings
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {detail.aggregated_results.key_findings.slice(0, 4).map((f, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, fontSize: 13 }}>
                    <span
                      style={{ color: "#0D9488", fontWeight: 700, flexShrink: 0 }}
                    >
                      {i + 1}.
                    </span>
                    <span
                      style={{
                        color: "var(--muted-foreground)",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      {f}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Model scores */}
          {Object.keys(detail.aggregated_results?.model_scores || {}).length > 0 && (
            <div>
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "var(--muted-foreground)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  marginBottom: 8,
                }}
              >
                Model Scores
              </p>
              <div className="flex flex-wrap gap-2.5">
                {Object.entries(detail.aggregated_results.model_scores).map(([model, score]) => (
                  <div
                    key={model}
                    style={{
                      padding: "8px 14px",
                      borderRadius: 10,
                      border: "1px solid var(--border)",
                      backgroundColor: "var(--background)",
                      textAlign: "center",
                      flex: "1 1 80px",
                      maxWidth: "140px",
                    }}
                  >
                    <div style={{ fontSize: 18, fontWeight: 700, color: "#0D9488" }}>
                      {Math.round(Number(score))}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--muted-foreground)",
                        textTransform: "capitalize",
                        marginTop: 2,
                      }}
                    >
                      {model}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface AgentRunsPageProps {
  isSidebarCollapsed?: boolean;
  onSidebarToggle?: (c: boolean) => void;
  activeBrandName?: string;
}

export function AgentRunsPage({
  isSidebarCollapsed: _s,
  onSidebarToggle: _t,
  activeBrandName,
}: AgentRunsPageProps) {
  const [runs, setRuns] = useState<AgentRunSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "complete" | "failed">("all");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRuns(await agentsApi.allRuns({ limit: 200, brandName: activeBrandName }));
    } catch (e: any) {
      setError(e.message || "Failed to load runs");
    } finally {
      setLoading(false);
    }
  }, [activeBrandName]);

  useEffect(() => { load(); }, [load]);

  const filtered = runs.filter((r) => {
    const matchSearch =
      !search ||
      r.agent_name?.toLowerCase().includes(search.toLowerCase()) ||
      r.brand_name?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === "all" || r.status === filter;
    return matchSearch && matchFilter;
  });

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "var(--background)" }}>
      {/* Header */}
      <div
        className="px-4 md:px-8 pt-6 md:pt-8 pb-5 md:pb-6 border-b"
        style={{ borderColor: "var(--border)", backgroundColor: "var(--background)" }}
      >
        <div
          className="flex items-center gap-2.5 mb-4 md:mb-5"
        >
          <div
            className="flex items-center justify-center flex-shrink-0"
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              backgroundColor: "rgba(13,148,136,0.1)",
              border: "1px solid rgba(13,148,136,0.2)",
            }}
          >
            <History style={{ width: 17, height: 17, color: "#0D9488" }} />
          </div>
          <span
            style={{
              fontSize: 13,
              color: "var(--muted-foreground)",
              fontWeight: 600,
            }}
          >
            Workflows · Run History
          </span>
        </div>

        {/* Title + refresh button — stacks on mobile */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4">
          <div className="min-w-0 flex-1">
            <h1
              style={{
                fontSize: "clamp(20px, 4.5vw, 24px)",
                fontWeight: 700,
                marginBottom: 6,
                lineHeight: 1.3,
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              Workflow History
            </h1>
            <p
              style={{
                color: "var(--muted-foreground)",
                fontSize: 14,
                lineHeight: 1.5,
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {activeBrandName ? (
                <>
                  Past workflow runs for <strong>{activeBrandName}</strong> — {runs.length} total
                </>
              ) : (
                <>All past workflow runs across every workflow — {runs.length} total</>
              )}
            </p>
          </div>
          <button
            onClick={load}
            className="w-full sm:w-auto flex-shrink-0"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              padding: "8px 14px",
              borderRadius: 9,
              border: "1px solid var(--border)",
              backgroundColor: "var(--muted)",
              fontSize: 13,
              cursor: "pointer",
              color: "var(--foreground)",
            }}
          >
            Refresh
          </button>
        </div>

        {/* Filters — search full-width on mobile, pills wrap */}
        <div className="flex flex-col md:flex-row gap-2.5 md:gap-3 mt-5">
          <div className="relative flex-1 md:min-w-[200px]">
            <Search
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                width: 14,
                height: 14,
                color: "var(--muted-foreground)",
              }}
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by workflow name or brand…"
              style={{
                width: "100%",
                padding: "8px 10px 8px 32px",
                borderRadius: 9,
                border: "1px solid var(--border)",
                backgroundColor: "var(--muted)",
                fontSize: 13,
                outline: "none",
                boxSizing: "border-box",
                color: "var(--foreground)",
              }}
            />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {(["all", "complete", "failed"] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className="flex-1 md:flex-initial whitespace-nowrap"
                style={{
                  padding: "8px 14px",
                  borderRadius: 9,
                  border: `1px solid ${filter === f ? "#0D9488" : "var(--border)"}`,
                  backgroundColor:
                    filter === f ? "rgba(13,148,136,0.08)" : "var(--muted)",
                  color: filter === f ? "#0D9488" : "var(--muted-foreground)",
                  fontSize: 13,
                  fontWeight: filter === f ? 600 : 400,
                  cursor: "pointer",
                  textTransform: "capitalize",
                }}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 md:px-8 py-5 md:py-6">
        {loading && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 60,
            }}
          >
            <Loader2
              style={{ width: 28, height: 28, color: "#0D9488" }}
              className="animate-spin"
            />
          </div>
        )}

        {error && !loading && (
          <div
            className="flex items-start gap-2.5 p-4 mb-4"
            style={{
              borderRadius: 10,
              backgroundColor: "rgba(239,68,68,0.08)",
              border: "1px solid rgba(239,68,68,0.2)",
            }}
          >
            <AlertCircle
              style={{
                width: 18,
                height: 18,
                color: "#EF4444",
                flexShrink: 0,
                marginTop: 2,
              }}
            />
            <p
              style={{
                fontSize: 13,
                color: "#EF4444",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {error}
            </p>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div
            className="flex flex-col items-center justify-center text-center"
            style={{
              padding: "60px 20px",
              color: "var(--muted-foreground)",
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 14,
                border: "2px dashed var(--border)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 14,
              }}
            >
              <GitBranch style={{ width: 22, height: 22, opacity: 0.4 }} />
            </div>
            <p
              style={{
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 6,
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {search || filter !== "all"
                ? "No runs match your filter"
                : activeBrandName
                ? `No runs yet for ${activeBrandName}`
                : "No runs yet"}
            </p>
            <p
              style={{
                fontSize: 12,
                maxWidth: 360,
                lineHeight: 1.5,
              }}
            >
              {search || filter !== "all"
                ? "Try adjusting your search or filter."
                : "Run a workflow from the AI Workflows page to see history here."}
            </p>
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {filtered.map((run) => (
              <RunRow key={run.id} run={run} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}